import React, { useRef, useEffect, useState } from 'react';
import './VisionMission.css';

function VisionMission() {
  const scrollRef = useRef();
  const containerRef = useRef();
  const [isActive, setIsActive] = useState(false);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  // Ensure it always starts from the first section
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
    }
  }, []);

  // Detect when section is visible
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      const isInView = rect.top < windowHeight && rect.bottom > 0;

      const sectionCenter = rect.top + rect.height / 2;
      const viewportCenter = windowHeight / 2;
      const distanceFromCenter = Math.abs(sectionCenter - viewportCenter);

      const relaxedTolerance = 200;
      const shouldActivate =
        distanceFromCenter <= relaxedTolerance || (isInView && rect.height <= windowHeight);

      setIsActive(shouldActivate);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Track horizontal scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (!scrollRef.current) return;

      const scrollElement = scrollRef.current;
      const sectionWidth = scrollElement.clientWidth;
      const currentScroll = scrollElement.scrollLeft;
      const currentSection = Math.round(currentScroll / sectionWidth);

      setIsAtStart(currentSection === 0);
      setIsAtEnd(currentSection === 2);

      if (currentSection === 2 || currentSection === 0) {
        document.body.style.overflow = '';
      }
    };

    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll);
      handleScroll();
    }

    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  // Desktop only: Lock vertical scroll & enable horizontal scroll on wheel
  useEffect(() => {
    if (window.innerWidth <= 768) return; // Skip for mobile

    const lockScroll = () => {
      document.body.style.overflow = 'hidden';
    };

    const unlockScroll = () => {
      document.body.style.overflow = '';
    };

    const handleWheel = (e) => {
      if (!scrollRef.current || !isActive) return;

      const scrollElement = scrollRef.current;
      const isScrollingDown = e.deltaY > 0;
      const isScrollingUp = e.deltaY < 0;

      const sectionWidth = scrollElement.clientWidth;
      const currentScroll = scrollElement.scrollLeft;
      const currentSection = Math.round(currentScroll / sectionWidth);

      if (isScrollingDown && !isAtEnd) {
        e.preventDefault();
        lockScroll();
        const nextSection = Math.min(currentSection + 1, 2);
        scrollElement.scrollTo({
          left: nextSection * sectionWidth,
          behavior: 'smooth',
        });
      } else if (isScrollingUp && !isAtStart) {
        e.preventDefault();
        lockScroll();
        const prevSection = Math.max(currentSection - 1, 0);
        scrollElement.scrollTo({
          left: prevSection * sectionWidth,
          behavior: 'smooth',
        });
      } else {
        unlockScroll();
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleWheel);
      document.body.style.overflow = '';
    };
  }, [isActive, isAtStart, isAtEnd]);

  // Mobile: No forced scroll, let native swipe work
  useEffect(() => {
    if (!isActive || window.innerWidth > 768) return;
    // No JS needed for manual swipe
  }, [isActive]);

  return (
    <div className="vision-container" ref={containerRef}>
      <div className="vision-scroll-area" ref={scrollRef}>
        <div className="scroll-wrapper">
          <div className="section">
            <div style={{ fontSize: '28px' }} className="left poppinsx">Our Vision</div>
            <div style={{ textAlign: 'left', fontSize: '18px' }} className="right poppin">
              To become the industry leader in setting modern trends and developing brand identities,
              becoming the preferred choice for leaders seeking trendy and impactful branding solutions.
            </div>
          </div>

          <div className="section">
            <div style={{ fontSize: '28px' }} className="left poppinsx">Our Mission</div>
            <div style={{ textAlign: 'left', fontSize: '18px' }} className="right poppin">
              We are dedicated to offering comprehensive branding and identity creation services for individuals,
              products, and organizations, empowering them to become trendsetters and industry leaders in their respective fields.
            </div>
          </div>

          <div className="section">
            <div style={{ fontSize: '28px' }} className="left poppinsx">Core Values</div>
            <div className="right">
              <ul style={{ textAlign: 'left', fontSize: '18px' }} className='poppin'>
                <li><strong >Value-Based Branding :</strong> <span style={{color:'gray'}}>We focus on creating brands that deliver real value to businesses and society.</span></li>
                <li><strong>Social Constructivism :</strong><span style={{color:'gray'}}> We believe that branding shapes culture, perceptions, and industry trends, and we strive to lead this transformation.</span></li>
                <li><strong>Team Spirit :</strong><span style={{color:'gray'}}> We work with collaboration, innovation, and shared success at the core of our operations.</span></li>
                <li><strong>Creating Leaders :</strong> <span style={{color:'gray'}}>We empower our clients to lead their industries with powerful and modern branding.</span></li>
                <li><strong>Partners in Sustainable Growth :</strong> <span style={{color:'gray'}}>We build brands that not only succeed today but also sustain and grow in the future.
</span></li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {isActive && (
        <>
          <div className="scroll-progress">
            <div className="progress-dots">
              <span className={`dot ${isAtStart ? 'active' : ''}`}></span>
              <span className={`dot ${!isAtStart && !isAtEnd ? 'active' : ''}`}></span>
              <span className={`dot ${isAtEnd ? 'active' : ''}`}></span>
            </div>
            <div className="scroll-hint">
              {isAtStart && "Scroll down to explore →"}
              {!isAtStart && !isAtEnd && "← Scroll to navigate →"}
              {isAtEnd && "Scroll down to continue ↓"}
            </div>
          </div>

          <div className="center-indicator">
            <div className="center-dot"></div>
          </div>
        </>
      )}
    </div>
  );
}

export default VisionMission;
